import type { CSSProperties } from 'react';
import { ChartTable } from '../ChartTable/ChartTable.tsx';
import type { ChartPoint } from '../ColumnChart/chart-point.ts';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import { EmptyState } from '../EmptyState/EmptyState.tsx';
import styles from './DonutChart.module.css';

export type DonutChartProps = {
  label: string;
  slices: readonly ChartPoint[];
  emptyTitle: string;
  loading?: boolean;
};

const SERIES_COLORS = ['chart2', 'chart5', 'chart3', 'chart6', 'chart4', 'chart1'] as const;

const FULL_TURN = 100;

const QUARTER_TURN = 25;

const SLICE_GAP = 0.4;

type Arc = {
  readonly slice: ChartPoint;
  readonly color: string;
  readonly start: number;
  readonly share: number;
};

const colorOf = (index: number): string =>
  `var(--color-${SERIES_COLORS[index % SERIES_COLORS.length] ?? 'chart1'})`;

const arcsOf = (slices: readonly ChartPoint[]): readonly Arc[] => {
  const total = slices.reduce((sum, slice) => sum + Math.max(slice.value, 0), 0);
  return slices.reduce<readonly Arc[]>((arcs, slice, index) => {
    const previous = arcs.at(-1);
    const start = previous === undefined ? 0 : previous.start + previous.share;
    const share = total > 0 ? (Math.max(slice.value, 0) / total) * FULL_TURN : 0;
    return [...arcs, { slice, color: colorOf(index), start, share }];
  }, []);
};

const strokeOf = (arc: Arc, gap: number): CSSProperties => ({
  stroke: arc.color,
  strokeDasharray: `${String(Math.max(arc.share - gap, 0))} ${String(FULL_TURN - Math.max(arc.share - gap, 0))}`,
  strokeDashoffset: String(QUARTER_TURN - arc.start),
});

export const DonutChart = ({ label, slices, emptyTitle, loading = false }: DonutChartProps) => {
  if (loading) return <Skeleton height="16rem" />;
  const arcs = arcsOf(slices).filter((arc) => arc.share > 0);
  if (arcs.length === 0) return <EmptyState title={emptyTitle} />;
  const gap = arcs.length > 1 ? SLICE_GAP : 0;
  return (
    <figure className={styles['donut']}>
      <figcaption className="visually-hidden">{label}</figcaption>
      <ul className={styles['legend']} aria-hidden="true">
        {arcs.map((arc) => (
          <li key={arc.slice.key}>
            <span className={styles['swatch']} style={{ background: arc.color }} />
            {arc.slice.label}
          </li>
        ))}
      </ul>
      <svg className={styles['ring']} viewBox="0 0 42 42" aria-hidden="true">
        {arcs.map((arc) => (
          <circle
            key={arc.slice.key}
            className={styles['slice']}
            cx="21"
            cy="21"
            r="15.9155"
            style={strokeOf(arc, gap)}
          >
            <title>{`${arc.slice.label} · ${arc.slice.display}`}</title>
          </circle>
        ))}
      </svg>
      <ChartTable label={label} points={slices} />
    </figure>
  );
};
